import {
  pixelCitizenCatalog as cat, pcChoiceSelections, pcSwatches, validatePcCustomization, PC_LAYERS,
  normalizeCampusAvatar,
  type AvatarAppearance, type CampusAppearance, type CampusAvatar, type PcAppearance, type PcCustomization,
  type PcLayerId, type PcSlot,
} from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';

/**
 * Apparence Pixel Citizen côté interface : valeur par défaut, tirage au
 * hasard, enregistrement. Les choix et couleurs viennent du catalogue partagé
 * (`@campus/core`), validé à l'identique par l'API.
 */

/** Choix avec ses couleurs par défaut (premier échantillon de chaque sélection). */
export function withDefaultColors(layer: PcLayerId, choice: string, pick = (_n: number) => 0): PcSlot {
  return {
    choice,
    colors: pcChoiceSelections(layer, choice).map((spec) => {
      const swatches = pcSwatches(spec);
      return swatches[pick(swatches.length) % swatches.length]!;
    }),
  };
}

const basic = cat.palettes['basic-clothes']!;

/** Avatar de départ : tenue simple, sans accessoire. */
export const DEFAULT_PC: PcCustomization = {
  'skin-color': [cat.bio.skin[4]!],
  'hair-color': [cat.bio.hair[4]!, cat.bio.hair[4]!, cat.bio.hair[4]!],
  'eye-color': [cat.bio.eyes[1]!],
  body: { choice: 'medium-m', colors: [] },
  head: { choice: 'standard', colors: [] },
  nose: { choice: 'small', colors: [] },
  eyes: { choice: 'curious', colors: [] },
  'hair-front': { choice: 'side-part', colors: [] },
  'hair-back': '%none%',
  'facial-hair': '%none%',
  'face-accessory': '%none%',
  headwear: '%none%',
  top: { choice: 't-shirt', colors: [basic[5]!] },
  outerwear: '%none%',
  bottom: { choice: 'jeans', colors: [cat.palettes.denim![0]!] },
  footwear: { choice: 'sneakers', colors: [basic[0]!, basic[2]!] },
};

const rand = (n: number) => Math.floor(Math.random() * n);

/** Tirage au hasard parmi les choix proposés (calques facultatifs : une chance sur deux d'être vides). */
export function randomPc(): PcCustomization {
  const hair = cat.bio.hair[rand(cat.bio.hair.length)]!;
  const out: Record<string, unknown> = {
    'skin-color': [cat.bio.skin[rand(cat.bio.skin.length)]!],
    'hair-color': [hair, hair, hair],
    'eye-color': [cat.bio.eyes[rand(cat.bio.eyes.length)]!],
  };
  for (const layer of PC_LAYERS) {
    const def = cat.layers[layer];
    const choices = def.choices.filter((c) => !c.hidden);
    // Couvre-chef et barbe : plus rares, sinon presque tout le monde en aurait
    const noneOdds = layer === 'headwear' || layer === 'facial-hair' ? 0.8 : 0.5;
    if (def.optional && Math.random() < noneOdds) { out[layer] = '%none%'; continue; }
    out[layer] = withDefaultColors(layer, choices[rand(choices.length)]!.id, rand);
  }
  return out as unknown as PcCustomization;
}

/** Brouillon de l'éditeur : avatar Pixel Citizen (v2) ou monde du campus (v3). */
export type AvatarDraft = { v: 2; pc: PcCustomization } | { v: 3; campus: CampusAvatar };

/** Brouillon de départ de l'éditeur : l'apparence enregistrée si elle est valide, sinon Pixel Citizen par défaut. */
export function draftFrom(appearance: AvatarAppearance | null | undefined): AvatarDraft {
  const campus = (appearance as Partial<CampusAppearance> | null | undefined)?.campus;
  if (campus) {
    const c = normalizeCampusAvatar(campus);
    if (c.ok) return { v: 3, campus: c.value };
  }
  const pc = (appearance as Partial<PcAppearance> | null | undefined)?.pc;
  const r = pc ? validatePcCustomization(pc) : null;
  return { v: 2, pc: r?.ok ? r.value : DEFAULT_PC };
}

/** Corps des requêtes d'avatar (`/avatars/preview`, `/avatars/sheets`). */
export const draftBody = (draft: AvatarDraft) => (draft.v === 3 ? { campus: draft.campus } : { pc: draft.pc });

export class AvatarSaveError extends Error {}

/**
 * Enregistre l'apparence : profil du membre (planche assemblée et stockée par
 * l'API), ou planche seule pour un invité (pas de profil persistant).
 */
export async function saveAvatar(draft: AvatarDraft, headers: Record<string, string>): Promise<PcAppearance | CampusAppearance> {
  const h = { ...headers, 'Content-Type': 'application/json' };
  const res = await fetch(`${API_BASE}/auth/me/appearance`, { method: 'PATCH', headers: h, body: JSON.stringify(draft) });
  if (res.ok) return (await res.json() as { appearance: PcAppearance | CampusAppearance }).appearance;
  if (res.status === 403) {
    const sheetRes = await fetch(`${API_BASE}/avatars/sheets`, { method: 'POST', headers: h, body: JSON.stringify(draftBody(draft)) });
    if (sheetRes.ok) return { ...draft, sheet: (await sheetRes.json() as { sheet: string }).sheet };
  }
  throw new AvatarSaveError(res.status === 503
    ? 'Les avatars sont momentanément indisponibles. Réessaie dans un instant.'
    : 'Impossible d\'enregistrer ton avatar. Réessaie.');
}
